// DTLS-fingerprint signature verification
//
// The box signs a message that binds its peer_id to the DTLS
// fingerprint it will present in the WebRTC handshake and the
// session_id minted by POST /v1/connect. The bootstrap MUST verify
// that signature before handing the SDP answer to
// `RTCPeerConnection.setRemoteDescription`.
//
// Byte layout (mirrors crates/identity/src/proofs.rs `build_dtls_fp_payload`):
//
//   "p2claw-dtls-fp-v1" || 0x0A || pubkey (32 bytes)
//     || u16_be(len(session_id)) || session_id (UTF-8)
//     || u8(fp_alg) || fp (fp_alg-specific length; 32 bytes for SHA-256)
//
// Primary verification path: native WebCrypto `Ed25519`. Modern
// browsers (Chrome ≥ 113, Safari ≥ 17, Firefox ≥ 129) all ship it.
//
// Fallback path: on
// browsers without native Ed25519 (Firefox 106-128, Safari 15/16,
// older Chrome), we dynamic-import `@noble/ed25519` and verify in
// JS. The chunk is the same one noble already pulls into the bundle
// for alias-binding verification — Vite splits it once, dynamic
// import caches across calls, so the second consumer (this one)
// gets the loaded module synchronously after the first
// (alias-binding) has finished. ~5 KB gzip; modern browsers never
// download it.
//
// Tagged result distinguishes "signature failed to verify"
// (`invalid` — at least one verifier ran end-to-end and rejected,
// real MITM signal) from "no path could run" (`unsupported` — old
// browser whose dynamic-import or no-WebCrypto-at-all paths both
// failed, surface a `browser-too-old` UI, never a MITM message).

/** Exact domain-separator bytes baked into the signed payload. */
export const DTLS_FP_DOMAIN_SEP = "p2claw-dtls-fp-v1";

/** Length byte that separates the domain-separator prefix from the body. */
export const DTLS_FP_SEPARATOR_BYTE = 0x0a;

/** SHA-256 fingerprint algorithm code. Matches OpenSSL / browser DTLS. */
export const FP_ALG_SHA256 = 0x01;

/**
 * Build the canonical byte payload that gets signed / verified.
 *
 * Mirrors `build_dtls_fp_payload` in crates/identity/src/proofs.rs.
 */
export function buildDtlsFpPayload(input: {
  peerIdBytes: Uint8Array; // 32 bytes
  sessionId: string;
  fpAlg: number;
  fp: Uint8Array;
}): Uint8Array {
  const { peerIdBytes, sessionId, fpAlg, fp } = input;
  if (peerIdBytes.length !== 32) {
    throw new Error(
      `dtls-fp: peer_id must be 32 bytes (got ${peerIdBytes.length})`,
    );
  }
  const sepBytes = new TextEncoder().encode(DTLS_FP_DOMAIN_SEP);
  const sidBytes = new TextEncoder().encode(sessionId);
  if (sidBytes.length > 0xffff) {
    throw new Error(`dtls-fp: session_id too long (${sidBytes.length} bytes)`);
  }
  if (fpAlg < 0 || fpAlg > 0xff) {
    throw new Error(`dtls-fp: fp_alg out of range (${fpAlg})`);
  }

  const total = sepBytes.length + 1 + 32 + 2 + sidBytes.length + 1 + fp.length;
  const out = new Uint8Array(total);
  let p = 0;
  out.set(sepBytes, p);
  p += sepBytes.length;
  out[p++] = DTLS_FP_SEPARATOR_BYTE;
  out.set(peerIdBytes, p);
  p += 32;
  out[p++] = (sidBytes.length >>> 8) & 0xff;
  out[p++] = sidBytes.length & 0xff;
  out.set(sidBytes, p);
  p += sidBytes.length;
  out[p++] = fpAlg & 0xff;
  out.set(fp, p);
  return out;
}

/** Tagged DTLS-fingerprint verification result.
 *
 *   * `{ ok: true, via: "native" }`     — modern browser, native
 *                                          WebCrypto verified.
 *   * `{ ok: true, via: "fallback" }`   — old browser, noble verified.
 *   * `{ ok: false, reason: "invalid" }`— signature didn't match. At
 *                                         least one verifier ran
 *                                         end-to-end and rejected.
 *                                         Surface MITM-grade copy.
 *   * `{ ok: false, reason: "unsupported" }` — neither native nor
 *                                              fallback could run.
 *                                              Surface "browser too
 *                                              old" copy.
 *
 * Mirrors `BindingVerifyResult` in `binding-verify.ts` so callers can
 * apply the same MITM-vs-old-browser dispatch.
 */
export type DtlsFpVerifyResult =
  | { ok: true; via: "native" | "fallback" }
  | { ok: false; reason: "invalid" | "unsupported" };

/** Subset of `@noble/ed25519` we use. Local structural alias of the
 *  shape `binding-verify.ts` already imports — keeps the module
 *  self-contained and avoids forcing test code to re-import a name
 *  from a sibling file. */
export interface NobleEd25519 {
  verifyAsync(
    signature: Uint8Array,
    payload: Uint8Array,
    rootPubkey: Uint8Array,
  ): Promise<boolean>;
}

export interface VerifyDtlsFpSignatureInput {
  peerIdBytes: Uint8Array;
  sessionId: string;
  fpAlg: number;
  fp: Uint8Array;
  /** Raw 64-byte Ed25519 signature. */
  signature: Uint8Array;
  /**
   * Optional SubtleCrypto override (lets tests pin a specific
   * implementation; prod code passes nothing and we read
   * `globalThis.crypto.subtle`).
   */
  subtle?: SubtleCrypto;
  /**
   * Called once when native is unsupported and we're about to load
   * the noble fallback. Mirrors the binding-verify hook;
   * production currently relies on the alias-binding call having
   * already pre-warmed the noble chunk on this same browser, so the
   * DTLS path's load is a cache hit and no overlay is needed. */
  onFallbackStart?: () => void;
  /**
   * Test seam — substitute the noble loader. Defaults to the
   * dynamic `import("@noble/ed25519")` Vite splits into its own
   * chunk so the bundle isn't paid by modern browsers.
   */
  loadFallback?: () => Promise<NobleEd25519>;
}

/**
 * Verify a DTLS-fingerprint signature. See [`DtlsFpVerifyResult`] for
 * return shape.
 *
 * Caller maps:
 *   * `{ ok: true, via: "native" | "fallback" }` — proceed with handshake.
 *   * `{ ok: false, reason: "invalid" }`         — `DtlsFingerprintMismatchError`.
 *   * `{ ok: false, reason: "unsupported" }`     — `WebcryptoUnsupportedError`.
 */
export async function verifyDtlsFpSignature(
  input: VerifyDtlsFpSignatureInput,
): Promise<DtlsFpVerifyResult> {
  const { peerIdBytes, sessionId, fpAlg, fp, signature } = input;

  if (signature.length !== 64) return { ok: false, reason: "invalid" };
  if (peerIdBytes.length !== 32) return { ok: false, reason: "invalid" };

  let payload: Uint8Array;
  try {
    payload = buildDtlsFpPayload({ peerIdBytes, sessionId, fpAlg, fp });
  } catch {
    // Oversized session_id or out-of-range fp_alg — treat as
    // verification failure, not a programmer error. A hostile relay
    // could send garbage.
    return { ok: false, reason: "invalid" };
  }

  // Native path: try WebCrypto Ed25519. If `importKey` throws, the
  // browser doesn't support Ed25519 (Firefox < 129, Safari < 17,
  // Chrome < 113) and we fall through to the noble path. Same logic
  // as `verifyAliasBinding`.
  const subtle = input.subtle ?? getSubtleOrNull();
  if (subtle !== null) {
    let nativeKey: CryptoKey | null = null;
    try {
      const keyBuf = new ArrayBuffer(peerIdBytes.byteLength);
      new Uint8Array(keyBuf).set(peerIdBytes);
      nativeKey = await subtle.importKey(
        "raw",
        keyBuf,
        { name: "Ed25519" },
        false,
        ["verify"],
      );
    } catch {
      // `importKey` throws either NotSupportedError (Ed25519 unknown)
      // or DataError (key bytes malformed). The latter is rare —
      // length validated above — so this is overwhelmingly the
      // unsupported case. Fall through to noble.
      nativeKey = null;
    }
    if (nativeKey !== null) {
      try {
        // Copy the signature + payload into fresh ArrayBuffers so the
        // DOM lib's strict `BufferSource` shape (no SharedArrayBuffer)
        // is satisfied and subtle owns its own memory.
        const sigBuf = new ArrayBuffer(signature.byteLength);
        new Uint8Array(sigBuf).set(signature);
        const payBuf = new ArrayBuffer(payload.byteLength);
        new Uint8Array(payBuf).set(payload);
        const ok = await subtle.verify({ name: "Ed25519" }, nativeKey, sigBuf, payBuf);
        return ok ? { ok: true, via: "native" } : { ok: false, reason: "invalid" };
      } catch {
        // `verify` itself threw — treat as `invalid`. Native ran far
        // enough that a falsy return would have been a clean
        // mismatch; a thrown error here usually means corrupted
        // input, not an unsupported browser.
        return { ok: false, reason: "invalid" };
      }
    }
  }

  // Fallback path. Native couldn't import the key (or `subtle` is
  // entirely unavailable). Hand control to the caller via
  // `onFallbackStart` (defensive — the alias-binding call should
  // already have triggered the overlay on boot), then dynamic-import
  // noble.
  input.onFallbackStart?.();
  const loader = input.loadFallback ?? defaultNobleLoader;
  let noble: NobleEd25519;
  try {
    noble = await loader();
  } catch {
    // No native, no fallback — true "unsupported". Caller surfaces
    // a "browser too old" message.
    return { ok: false, reason: "unsupported" };
  }
  try {
    const ok = await noble.verifyAsync(signature, payload, peerIdBytes);
    return ok ? { ok: true, via: "fallback" } : { ok: false, reason: "invalid" };
  } catch {
    // Noble's `verifyAsync` throws on malformed inputs (length pre-
    // validated, so this is largely defensive). Treat as `invalid`.
    return { ok: false, reason: "invalid" };
  }
}

/** Default loader. Dynamic-import lets Vite split @noble/ed25519
 *  into its own chunk that only old-browser sessions download. The
 *  same chunk `binding-verify.ts` imports — module-cache de-dups the
 *  fetch when both verify paths fall back on the same session. */
const defaultNobleLoader = (): Promise<NobleEd25519> =>
  import("@noble/ed25519") as Promise<NobleEd25519>;

/** Returns `globalThis.crypto.subtle` or `null` if WebCrypto is
 *  entirely unavailable. The completely-no-WebCrypto case is rare
 *  but possible (older Safari over insecure origin, exotic embedded
 *  environments); we drop straight to the fallback path rather than
 *  throwing. Mirrors `binding-verify.ts`'s helper. */
function getSubtleOrNull(): SubtleCrypto | null {
  const c = (globalThis as { crypto?: Crypto }).crypto;
  return c?.subtle ?? null;
}

/**
 * Probe for native Ed25519 support.
 *
 * **Deprecated for production callers.** The bootstrap used to
 * call this at boot and surface a hard failure when it returned
 * false; that path produced the false-positive "WebCrypto Ed25519 is
 * not available" error for any browser without native Ed25519, even
 * when noble would have worked. Production now skips the upfront
 * probe entirely — `verifyDtlsFpSignature` and `verifyAliasBinding`
 * both fall back to noble internally and signal `unsupported` only
 * when neither path can run.
 *
 * Kept exported for the cross-language vector tests in
 * `test/identity-verify.test.ts`, which guard themselves on it
 * (older Node WebCrypto without Ed25519 — vectors get skipped).
 */
export async function hasEd25519Support(subtle?: SubtleCrypto): Promise<boolean> {
  const s = subtle ?? getSubtleOrNull();
  if (s === null) return false;
  try {
    await s.generateKey({ name: "Ed25519" }, true, ["sign", "verify"]);
    return true;
  } catch {
    return false;
  }
}
